<script setup lang="ts">
import type { StopSectionInstance } from '@/composables/jobEdit/useJobEditStops';
import type { StopItem } from '@/models/jobEdit';

import { computed } from 'vue';

import JobEditAccordionSection from '@/components/sections/JobEditAccordionSection.vue';
import JobEditStopSection from '@/components/sections/JobEditStopSection.vue';
import { useJobEditStops } from '@/composables/jobEdit/useJobEditStops';
import { useJobEditStore } from '@/stores/useJobEditStore';

export interface Props {
  stops?: StopItem[];
}

const props = withDefaults(defineProps<Props>(), {
  stops: () => [],
});

const jobEditStore = useJobEditStore();

const {
  isEditing,
  isLoading,
  startEditing,
  cancelAll,
  saveAll,
  registerStopRef,
} = useJobEditStops();

const stopNamesText = computed(() => {
  return props.stops
    .map((stop) => stop.name || '')
    .filter(Boolean)
    .join(' / ');
});

async function handleSave(): Promise<void> {
  try {
    await saveAll();
  } catch {
    // Error already handled: saveEditing opens a dialog and logs to Sentry
  }
}

function setStopRef(stopNumber: number, el: unknown): void {
  registerStopRef(stopNumber, el as StopSectionInstance | null);
}
</script>

<template>
  <JobEditAccordionSection
    :class="[$style.jobEditStopsSection, { [$style.editing]: isEditing }]"
    title="STOPS"
    :is-editing="isEditing"
    :is-loading="isLoading"
    editable
    data-testid="jobEditStopsSection"
    @save="handleSave"
    @cancel="cancelAll"
    @edit-btn-click="startEditing"
  >
    <template v-if="stopNamesText" #collapsedContent>
      <span>{{ stopNamesText }}</span>
    </template>

    <div :class="$style.stopsRow">
      <JobEditStopSection
        v-for="(stop, index) in props.stops"
        :key="`stop-${stop.stop_number}`"
        :ref="(el) => setStopRef(stop.stop_number, el)"
        :initial-data="stop"
        :stop-number="stop.stop_number"
        :stop-type="
          index === 0
            ? 'Pickup'
            : index === props.stops.length - 1
              ? 'Delivery'
              : 'Stop'
        "
        :total-stops="props.stops.length"
        :editing-override="isEditing"
        @move-left="
          jobEditStore.swapStops(stop.stop_number, stop.stop_number - 1)
        "
        @move-right="
          jobEditStore.swapStops(stop.stop_number, stop.stop_number + 1)
        "
        @delete="jobEditStore.removeStop(stop.stop_number)"
      />

      <div
        v-if="props.stops.length < 3 && isEditing"
        :class="$style.addStopCard"
        @click="jobEditStore.addStop()"
      >
        <v-icon icon="mdi-plus" size="large" />
        <span>ADD STOP</span>
      </div>
    </div>
  </JobEditAccordionSection>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditStopsSection {
  margin-bottom: 24px;
}

.editing {
  position: relative;
  z-index: 102;
}

.stopsRow {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.addStopCard {
  flex: 1 1 0;
  align-self: stretch;
  border: 2px dashed colors.$color-gray-300;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: colors.$color-text-secondary;

  &:hover {
    border-color: colors.$color-brand-500;
    color: colors.$color-brand-500;
  }
}
</style>
